<script setup>
import { computed, ref, onMounted } from 'vue';

const emit = defineEmits(['update:checked', 'update:modelValue', 'input']);

// Generate a unique ID for this checkbox instance
const uniqueId = ref('');

onMounted(() => {
    // Generate a 5-digit random number on component mount
    uniqueId.value = `checkbox_${Math.floor(10000 + Math.random() * 90000)}`;
});

const props = defineProps({
    // New Vue 3 prop
    checked: {
        type: [Array, Boolean],
        default: false,
    },
    // Old Vue 2 v-model prop
    modelValue: {
        type: [Array, Boolean],
        default: false,
    },
    // For backward compatibility with v-model in Vue 2
    value: {
        type: [String, Boolean, Number],
        default: null,
    },
    // Support for the 'wrapped' attribute
    wrapped: {
        type: Boolean,
        default: false,
    },
    // Support for the 'label' attribute
    label: {
        type: String,
        default: null,
    },
    // Support for the 'name' attribute
    name: {
        type: String,
        default: null,
    }
});

// Unified proxy that handles both Vue 2 and Vue 3 bindings
const proxyChecked = computed({
    get() {
        // Prioritize checked (Vue 3) if present, fall back to modelValue (Vue 2)
        return props.checked !== false ? props.checked : props.modelValue;
    },
    set(val) {
        // Emit all relevant events for both Vue 2 and Vue 3
        emit('update:checked', val);
        emit('update:modelValue', val);
        emit('input', val); // For Vue 2 compatibility
    },
});

// Helper function to determine if the checkbox should be checked
const isChecked = computed(() => {
    const modelValue = proxyChecked.value;
    
    if (Array.isArray(modelValue)) {
        return modelValue.includes(props.value);
    }
    
    return !!modelValue;
});
</script>

<template>
    <div 
        :class="{ 'flex items-center cursor-pointer select-none': wrapped }"
    >
        <input
            :id="uniqueId"
            v-model="proxyChecked"
            type="checkbox"
            :value="value"
            :name="name"
            class="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500 cursor-pointer"
        >
        <label 
            v-if="label" 
            :for="uniqueId"
            :class="{ 'ml-2 cursor-pointer select-none': wrapped }"
        >
            {{ label }}
        </label>
    </div>
</template>